import {Injectable} from '@angular/core';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';
import {LayerUtilService} from './layer-util.service';
import {SpatialUtilService} from '../services/spatial-util.service';
import {LookupService} from '../../common/service/lookup.service';
import {LegalParcelTypeLookup} from '../../common/models/legal-parcel-type.model';
import {EsriModuleService} from '../esri-nlr/services/esri-module.service';

/* eslint-disable @typescript-eslint/no-explicit-any */
@Injectable({
    providedIn: 'root'
})
export class PolygonLayerService {
    private readonly layerId = 'polygonFeatureLayer_layer';

    private readonly layerSelector = `#${this.layerId}`;

    private readonly featureClass = 'polygon';

    private readonly featureIdAttrName = this.layerUtilService.getFeatureIdAttrName(this.featureClass);

    private readonly featureTypeAttrName = this.layerUtilService.getFeatureTypeAttrName(this.featureClass);

    private getLegalParcelTypeLookups = (): LegalParcelTypeLookup[] =>
        this.lookupService.getLookups('legalParcelType').filter((l: LegalParcelTypeLookup) => l.id !== 97);

    private svgStyle: string;

    private trustedSvgStyle: SafeHtml;

    constructor(
        private domSanitizer: DomSanitizer,
        private esriModuleService: EsriModuleService,
        private layerUtilService: LayerUtilService,
        private lookupService: LookupService,
        private spatialUtilService: SpatialUtilService
    ) {}

    getSvgStyle(): SafeHtml {
        const scaleFactor = this.layerUtilService.getPixelScaleFactor();
        if (scaleFactor === 0) {
            return '';
        }

        const strokeWidthScaleFactor = this.layerUtilService.getStrokeWidthPixelScaleFactor(scaleFactor);
        const strokeWidth = 2.4 * strokeWidthScaleFactor;

        const newSvgStyle = [
            `${this.layerSelector} path[${this.featureTypeAttrName}="97"] {
                stroke:${this.layerUtilService.showFeatures() ? 'rgb(199,215,158)' : 'transparent'};
                stroke-opacity:1.0;
                stroke-width:${strokeWidth};
                fill: none;
            }`,
            `${this.layerSelector} path[${this.featureTypeAttrName}][data-selected] {
                fill:rgb(0, 255, 255);
                fill-opacity:0.3;
                stroke:rgb(0, 255, 255);
                stroke-opacity:0.01;
                stroke-width:${strokeWidth};
            }`,
            ...this.getLegalParcelTypeLookups().map(l => `${this.layerSelector} path[${this.featureTypeAttrName}="${l.id}"] {fill: transparent;}`)
        ].join('\n');

        if (newSvgStyle === this.svgStyle) {
            return this.trustedSvgStyle;
        }

        this.svgStyle = newSvgStyle;

        this.trustedSvgStyle = this.domSanitizer.bypassSecurityTrustHtml(this.svgStyle);

        return this.trustedSvgStyle;
    }

    init() {
        const esriModuleService = this.esriModuleService;
        const polygonFeatureLayer = this.spatialUtilService.getPolygonMapLayer();
        const layerUtilService = this.layerUtilService;
        const spatialUtilService = this.spatialUtilService;

        polygonFeatureLayer._drawShape = function (graphic: any, b: any, c: any, d: any) {
            if (!graphic.attributes) {
                return polygonFeatureLayer.prototype._drawShape.call(this, graphic, b, c, d);
            }

            let geom = graphic.geometry;
            const map: {extent: any; width: any; height: any; __visibleRect: any} = this._map;

            if (graphic.attributes.type === 97) {
                const offsetScaleFactor =
                    (layerUtilService.getStrokeWidthPixelScaleFactor(layerUtilService.getPixelScaleFactor()) * map.extent.getWidth()) / map.width;
                geom = esriModuleService.geometryEngine.offset(geom, -2.2 * offsetScaleFactor, 'meters', 'round');
            }

            let k = 0;
            let h = [];
            for (let l = b.length; k < l; k++) {
                h = h.concat(
                    esriModuleService.geometry.screenUtils._toScreenPath(
                        map.extent,
                        map.width,
                        map.height,
                        geom,
                        -map.__visibleRect.x + b[k],
                        -map.__visibleRect.y
                    )
                );
            }

            d = this._drawPath(c, d, h);
            this._rendererLimits && this._clipPolygon(d, geom);
            return d;
        };

        this.layerUtilService.addLayerEventHandler(polygonFeatureLayer, 'graphic-node-add', (evt: {graphic: any; node: any}) => {
            const featureId = evt.graphic.attributes.id;

            document
                .querySelectorAll(`${this.layerSelector} > path[${this.featureIdAttrName}="${featureId}"]`)
                .forEach(node => node.parentNode.removeChild(node));

            evt.node.setAttribute('id', layerUtilService.getFeatureNodeId(this.featureClass, featureId));
            evt.node.setAttribute(this.featureTypeAttrName, `${evt.graphic.attributes.type}`);
            evt.node.setAttribute(this.featureIdAttrName, `${featureId}`);
            this.layerUtilService.setSelectedAttr(evt.node, this.featureClass, featureId);
        });

        this.layerUtilService.addLayerEventHandler(polygonFeatureLayer, 'edits-complete', () => {
            const polygonIds = spatialUtilService.getPolygonGraphics().map((g: any) => g.attributes.id);

            document.querySelectorAll(`${this.layerSelector} > path[${this.featureIdAttrName}]`).forEach(path => {
                const polygonId = parseInt(path.getAttribute(this.featureIdAttrName));
                if (!polygonIds.includes(polygonId)) {
                    path.parentNode.removeChild(path);
                }
            });
        });

        this.layerUtilService.addLayerEventHandler(polygonFeatureLayer, 'click', (evt: any) => {
            this.layerUtilService.onClick(this.featureClass, evt);
        });

        this.layerUtilService.addLayerEventHandler(polygonFeatureLayer, 'dbl-click', (evt: any) => {
            this.layerUtilService.setSelectedAttr(evt.target, this.featureClass, evt.graphic.attributes.id);
        });

        return polygonFeatureLayer;
    }
}
